import { postUserMemory } from 'api/goodtrust/user/memory'
import { Button } from 'components/button/Button'
import { LifeStoriesBanners } from 'components/content/lifeStories/lifeStoriesBanners/LifeStoriesBanners'
import { SInfiniteLoader, SStackAsDropzone } from './LifeStoriesGallery.styled'
import { MemoryGalleryPlaceholder } from 'components/content/memory/memoryGalleryPlaceholder/MemoryGalleryPlaceholder'
import { StoryPortraitsPlaceholder } from 'components/content/memory/memoryGalleryPlaceholder/storyPortraitsPlaceholder/StoryPortraitsPlaceholder'
import { useMemoryViewer } from 'components/content/memory/memoryViewer/MemoryViewer'
import { isPictureTooBig, MEMORY_SIZE_LIMIT } from 'components/content/memory/utils'
import { useChooseSongModal } from 'components/content/singingPortraits/chooseSongModal/ChooseSongModal'
import { Gallery } from 'components/gallery/Gallery'
import { useChooseAnimationModal } from 'components/memory/chooseAnimationModal/ChooseAnimationModal'
import {
  AnimatedMemoryGalleryTabs,
  AnimationDriver,
  defaultMemoryThumbnailSize,
  generateMemoryGalleryTabContent,
  getIsSameMemory,
  getMemoryGalleryTab,
  LifeStoriesCustomTab,
  MemorySelectOption,
  MemoryType,
  SelectedMemory,
  useUserMemories,
} from 'components/memory/MemoryUtils'
import { BlockSpinner } from 'components/spinner/Spinner'
import { useStoryPortraitLogic } from 'components/storyPortraits/logic'
import { handleAndToastError, toastError, toastErrorString } from 'components/Toast'
import { useTranslation } from 'next-i18next'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { fireEvent } from 'utils/analytics'
import { ApiError } from 'utils/error'
import { FetchResponse, unwrapResponse } from 'utils/fetcher'
import { useLatestCallback } from 'utils/hooks'
import { pxToRem } from 'utils/styled'
import { SWRInfiniteResponse } from 'swr/infinite'
import { ApiType } from 'utils/types'

export function LifeStoriesGallery(props: {
  tabs: MemoryType[]
  customTabs?: LifeStoriesCustomTab[]
  openId?: string
  selectionMode?: MemorySelectOption
  value?: SelectedMemory[]
  onChange?: (value: SelectedMemory[]) => void
  customActions?: ReactNode
  className?: string
  placeholder?: ReactNode
  disableDropzone?: boolean
  defaultTab?: MemoryType
  hideTabs?: boolean
}) {
  const { t: tCommon } = useTranslation()
  const { t } = useTranslation('life_stories')
  const {
    tabs,
    customTabs,
    selectionMode = 'none',
    value,
    onChange,
    customActions,
    className,
    placeholder,
    disableDropzone,
    hideTabs,
  } = props

  const defaultTab = props.tabs[0] || getMemoryGalleryTab(tCommon).all

  const [memoriesBeingUploaded, setMemoriesBeingUploaded] = useState<File[]>([])

  const {
    allMemoriesSwr,
    allMemoriesMeta,
    photosSwr,
    photosMeta,
    singingHeadsSwr,
    singingHeadsMeta,
    livePortraitsSwr,
    livePortraitsMeta,
    storyPortraitsSwr,
    storyPortraitsMeta,
    refreshMemories,
  } = useUserMemories({ types: tabs })

  const [selectedTab, setSelectedTab] = useState<MemoryType>(defaultTab)

  const refreshGallery = async () => {
    await refreshMemories()
    if (inputRef?.current?.value) inputRef.current.value = ''
  }

  const [chooseSongModal, chooseSongViaModal] = useChooseSongModal()

  const [chooseAnimationModal, chooseAnimationViaModal] = useChooseAnimationModal()

  const handleDrop = async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      toastErrorString(
        fileRejections
          .map((file) =>
            file.errors
              .map((err) => {
                switch (err.code) {
                  case 'file-invalid-type': {
                    return tCommon('common.error_codes.file_invalid_type_image')
                  }
                  case 'file-too-large': {
                    return tCommon('common.error_codes.image_too_large')
                  }
                  case 'too-many-files': {
                    return tCommon('common.error_codes.pick_less_files')
                  }
                  default:
                    return err.message
                }
              })
              .join(', ')
          )
          .join('\n')
      )
    } else if (acceptedFiles.length) {
      const arePicturesTooBig = await Promise.all(
        acceptedFiles.map((file) => isPictureTooBig(file))
      )
      const imageOver10MPExists = arePicturesTooBig.some((isTooBig) => isTooBig)

      if (imageOver10MPExists) {
        toastError('image_10mp_limit')
      } else {
        // animate if user uploading single file and an animated tab is selected
        if (
          selectionMode === 'none' &&
          AnimatedMemoryGalleryTabs(tCommon).includes(selectedTab) &&
          acceptedFiles.length === 1
        ) {
          const file = acceptedFiles[0]
          if (file) {
            setMemoriesBeingUploaded([file])
            const [res] = await upload([file])
            let driver: AnimationDriver | null
            if (selectedTab.type === 'SINGING_PORTRAIT') {
              driver = await chooseSongViaModal()
              fireEvent('sh_photo_uploaded', {
                page: 'memories_dashboard',
                photo_source: 'file_upload',
              })
            } else {
              driver = await chooseAnimationViaModal()
            }
            viewMemory({
              viewedMemory: { memoryUuid: res?.json?.uuid },
              driverToAnimateWith: driver,
              source: 'memories_dashboard',
            })
          }
        } else {
          setMemoriesBeingUploaded(acceptedFiles)
          //selecting first non-animated tab
          const firstNonAnimatedTab = tabs.find(
            (t) => !AnimatedMemoryGalleryTabs(tCommon).includes(t)
          )
          if (firstNonAnimatedTab) {
            setSelectedTab(firstNonAnimatedTab)
          }
          upload(acceptedFiles)
        }
      }
    }
  }

  const upload = async (files: File[]) => {
    setMemoriesBeingUploaded(files)
    const responses = []
    try {
      for (const file of files) {
        const res = await postUserMemory(file).then(unwrapResponse)
        if (res.json?.id == null) throw new ApiError(res)
        responses.push(res)
        fireEvent('photo_uploaded', { page: 'memories_dashboard', photo_source: 'file_upload' })
      }
    } catch (err) {
      handleAndToastError(err, 'failed_to_upload_memories')
    } finally {
      await refreshGallery()
      setMemoriesBeingUploaded([])
    }
    return responses
  }

  const { getRootProps, getInputProps, isDragActive, inputRef, acceptedFiles } = useDropzone({
    onDrop: handleDrop,
    maxSize: MEMORY_SIZE_LIMIT,
    accept: 'image/png,image/jpeg',
    multiple: true,
    noClick: true,
    disabled: disableDropzone,
  })

  const uploadNewMemories = useCallback(() => inputRef.current?.click(), [inputRef])
  const storyChooseImageButton = useStoryPortraitLogic().useChooseImageButton()

  const onStoryChooseImageButtonClick = useLatestCallback(storyChooseImageButton.onClick)

  const memoryGalleryTab = getMemoryGalleryTab(tCommon)

  const tabsWithContent = useMemo(
    () =>
      tabs.map((tab) => {
        let meta
        let data: SWRInfiniteResponse<
          FetchResponse<ApiType['PaginatedResponseMemoryResponse']>
        >['data']
        switch (tab.key) {
          case memoryGalleryTab.photo.key:
            meta = photosMeta
            data = photosSwr.data
            break
          case memoryGalleryTab.singing.key:
            meta = singingHeadsMeta
            data = singingHeadsSwr.data
            break
          case memoryGalleryTab.live.key:
            meta = livePortraitsMeta
            data = livePortraitsSwr.data
            break
          case memoryGalleryTab.story.key:
            meta = storyPortraitsMeta
            data = storyPortraitsSwr.data
            break
          default:
            meta = allMemoriesMeta
            data = allMemoriesSwr.data
            break
        }

        const allData = data?.map((d) => d.json?.items ?? []).flat()

        const toNavigate = allData?.map((item) => {
          const animationOfThisType = item?.animated_files?.find(
            (file) => file.animation_type === tab.type
          )
          return {
            memoryUuid: item?.uuid,
            animationUuid: animationOfThisType?.uuid,
          }
        })

        return {
          tab,
          meta,
          placeholder: tab.type === 'STORY_PORTRAIT' ? <StoryPortraitsPlaceholder /> : undefined,
          toNavigate,
          action:
            tab.type === 'STORY_PORTRAIT' ? (
              <Button
                type="button"
                variant="text"
                data-testid="story-get-started"
                onClick={onStoryChooseImageButtonClick}
              >
                {t('life_stories.inside_page.gallery.get_started_button')}
              </Button>
            ) : (
              <Button
                type="button"
                variant="text"
                data-testid="gallery-upload-photo"
                onClick={uploadNewMemories}
              >
                {t('life_stories.inside_page.gallery.upload_photo_button')}
              </Button>
            ),
        }
      }),
    [
      allMemoriesMeta,
      allMemoriesSwr.data,
      livePortraitsMeta,
      livePortraitsSwr.data,
      memoryGalleryTab.live.key,
      memoryGalleryTab.photo.key,
      memoryGalleryTab.singing.key,
      memoryGalleryTab.story.key,
      onStoryChooseImageButtonClick,
      photosMeta,
      photosSwr.data,
      singingHeadsMeta,
      singingHeadsSwr.data,
      storyPortraitsMeta,
      storyPortraitsSwr.data,
      tabs,
      uploadNewMemories,
    ]
  )

  const selectedTabWithContent = tabsWithContent.find((t) => t.tab.key === selectedTab.key)

  const [memoryView, viewMemory] = useMemoryViewer(
    selectedTabWithContent?.toNavigate ?? [],
    refreshGallery,
    selectionMode !== 'none'
  )

  const onClick = useCallback(
    (clickedMemory: SelectedMemory) => {
      if (selectionMode === 'none' || !value) {
        viewMemory({
          viewedMemory: {
            memoryUuid: clickedMemory.uuid,
            animationUuid: clickedMemory.selectedAnimation?.uuid,
          },
        })
        return
      }

      if (selectionMode === 'single') {
        onChange?.([clickedMemory])
        return
      }

      const indexIfAlreadySelected = value?.findIndex((s) => getIsSameMemory(s, clickedMemory))

      if (value?.length && indexIfAlreadySelected !== -1) {
        onChange?.([
          ...value.slice(0, indexIfAlreadySelected),
          ...value.slice(indexIfAlreadySelected + 1),
        ])
      } else {
        onChange?.([...(value || []), clickedMemory])
      }
    },
    [onChange, selectionMode, value, viewMemory]
  )

  const tabsToDisplay = tabsWithContent.map((tabToDisplay) => {
    let data
    switch (tabToDisplay.tab.key) {
      case memoryGalleryTab.photo.key:
        data = photosSwr.data
        break
      case memoryGalleryTab.singing.key:
        data = singingHeadsSwr.data
        break
      case memoryGalleryTab.live.key:
        data = livePortraitsSwr.data
        break
      case memoryGalleryTab.story.key:
        data = storyPortraitsSwr.data
        break
      default:
        data = allMemoriesSwr.data
        break
    }
    return {
      ...tabToDisplay,
      content: generateMemoryGalleryTabContent({
        data,
        memoriesBeingUploaded,
        onClick,
        selectionMode,
        selectedMemories: value,
        animationType: tabToDisplay.tab.type,
        uploadNewMemories,
      }),
    }
  })

  return (
    <>
      {chooseSongModal}
      {chooseAnimationModal}
      {storyChooseImageButton.modal}

      {selectionMode === 'none' && (
        <LifeStoriesBanners
          css={{ marginBottom: '2rem' }}
          onUpload={async (uuid, driver) => {
            await refreshGallery()
            if (uuid)
              await viewMemory({
                viewedMemory: { memoryUuid: uuid },
                driverToAnimateWith: driver,
                source: 'banner_flow',
              })
          }}
        />
      )}

      <SStackAsDropzone
        className={className}
        space={`${pxToRem(14)}rem`}
        {...getRootProps({
          isDragActive,
          isAccepting: !memoriesBeingUploaded.length && !acceptedFiles.length,
        })}
      >
        {memoryView}
        <input ref={inputRef} {...getInputProps()} id="photo-upload-button" />
        {!selectedTabWithContent || !selectedTabWithContent?.meta?.isLoadingInitial ? (
          <Gallery
            hideTabs={hideTabs}
            rowHeight={defaultMemoryThumbnailSize}
            tabContentBasis="50vh"
            tabs={[
              ...tabsToDisplay.map((t) => ({
                label: t.tab.label,
                key: t.tab.key,
                content: t.content,
                footer: t.meta && <SInfiniteLoader metaData={t.meta} />,
                action: customActions || t.action,
                placeholder: t.placeholder,
              })),
              ...(customTabs || []),
            ]}
            onTabSelected={(key) => setSelectedTab(tabs.find((t) => t.key === key) || defaultTab)}
            placeholder={
              placeholder || (
                <MemoryGalleryPlaceholder
                  onClick={uploadNewMemories}
                  onUploadedSelfie={(file) => handleDrop([file], [])}
                />
              )
            }
            noShowPlaceholder
          />
        ) : (
          <BlockSpinner />
        )}
      </SStackAsDropzone>
    </>
  )
}
